// File: /var/www/app2/frontend/src/components/Reports.tsx

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import "../styles/Reports.css";
import { User } from "../App";
import { generateReportPDF } from "../utils/generateReportPDF";

// Interfaces for data structures
interface Missionary {
  id: string;
  name: string;
  number: string;
}

interface ReportData {
  heardGospel?: number;
  receivedJesus?: number;
  baptized?: number;
  [key: string]: any;
}

// A single planted church from the field app's Church Planting Details
// prompt (village, GPS location, believers count) — passed through to the
// PDF export so it isn't limited to the plain count in ReportData.
interface ChurchPlant {
  id: number;
  village_name: string | null;
  people_group: string | null;
  church_name: string;
  start_month: number | null;
  start_year: number | null;
  believers_count: number;
  gps_lat: number | null;
  gps_lng: number | null;
}

interface Report {
  id: string;
  missionary_id?: string;
  missionary_number: string;
  missionary_name: string;
  state: string;
  location: string;
  year: number;
  month: number;
  status: "review" | "approved" | "rejected";
  data?: ReportData;
  submission_date: string;
  approved_date?: string;
  rejected_date?: string;
  church_plants?: ChurchPlant[];
}

interface FilterState {
  year: string;
  month: string;
  status: string;
  missionaryId: string;
}

interface SortConfig {
  key: string;
  direction: "asc" | "desc";
}

interface MonthOption {
  value: string;
  label: string;
}

interface ReportsProps {
  user: User;
}

function Reports({ user }: ReportsProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [missionaries, setMissionaries] = useState<Missionary[]>([]);
  const [filters, setFilters] = useState<FilterState>({
    year: new Date().getFullYear().toString(),
    month: "",
    status: "",
    missionaryId: "",
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "number",
    direction: "asc",
  });
  const hasRestoredScroll = useRef(false);

  // Continuously remember scroll position so returning from a report detail
  // page (or any other navigation away) lands back where the user was,
  // instead of at the top of a long list.
  useEffect(() => {
    const saveScroll = () => {
      sessionStorage.setItem("hoa_reports_scrollY", String(window.scrollY));
    };
    window.addEventListener("scroll", saveScroll, { passive: true });
    return () => window.removeEventListener("scroll", saveScroll);
  }, []);

  // Restore scroll position once, after the first successful load following
  // a fresh mount (e.g. navigating back from a report's detail view).
  useEffect(() => {
    if (loading || hasRestoredScroll.current) return;
    hasRestoredScroll.current = true;
    const saved = sessionStorage.getItem("hoa_reports_scrollY");
    if (saved) {
      requestAnimationFrame(() => window.scrollTo(0, parseInt(saved, 10)));
    }
  }, [loading]);

  // Parse query parameters for initial filters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const newFilters: FilterState = {
      year: params.get("year") || new Date().getFullYear().toString(),
      month: params.get("month") || "",
      status: params.get("status") || "",
      missionaryId: params.get("missionaryId") || "",
    };
    setFilters(newFilters);
  }, [location.search]);

  useEffect(() => {
    fetchReports();
    fetchMissionaries();
  }, [filters, user]);

  const fetchReports = async (): Promise<void> => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.year) params.append("year", filters.year);
      if (filters.month) params.append("month", filters.month);
      if (filters.status) params.append("status", filters.status);
      if (filters.missionaryId)
        params.append("missionaryId", filters.missionaryId);

      const response = await axios.get<{ reports: Report[] }>(
        `/api/reports?${params.toString()}`
      );
      setReports(response.data.reports || []);
      setError("");
    } catch (error: any) {
      console.error("Failed to fetch reports:", error);
      setError("Failed to load reports. Please try again.");
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = user.role === "admin" || user.role === "superadmin" || user.role === "system_admin";

  const fetchMissionaries = async (): Promise<void> => {
    try {
      const url = isAdmin
        ? "/api/missionaries"
        : `/api/users/${user.id}/missionaries`;
      const response = await axios.get<{ missionaries: Missionary[] }>(url);
      setMissionaries(response.data.missionaries || []);
    } catch (error: any) {
      console.error("Failed to fetch missionaries:", error);
    }
  };

  const handleFilterChange = (field: keyof FilterState, value: string): void => {
    setFilters({
      ...filters,
      [field]: value,
    });
  };

  const handleViewReport = (reportId: string): void => {
    navigate(`/reports/${reportId}`);
  };

  const handleApproveReport = async (
    reportId: string,
    e: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to approve this report?")) {
      return;
    }

    try {
      await axios.patch(`/api/reports/${reportId}/status`, {
        status: "approved",
      });
      // Refresh reports
      fetchReports();
      alert("Report approved successfully!");
    } catch (error: any) {
      alert(
        "Failed to approve report: " +
          (error.response?.data?.error || error.message)
      );
    }
  };

  const handleRejectReport = async (
    reportId: string,
    e: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    e.stopPropagation();
    const reason = prompt("Please provide a reason for rejection:");
    if (!reason) {
      return;
    }

    try {
      await axios.patch(`/api/reports/${reportId}/status`, {
        status: "rejected",
        reason: reason,
      });
      // Refresh reports
      fetchReports();
      alert("Report rejected.");
    } catch (error: any) {
      alert(
        "Failed to reject report: " +
          (error.response?.data?.error || error.message)
      );
    }
  };

  const handleDeleteReport = async (
    report: Report,
    e: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete the report for ${report.missionary_name} (${report.month}/${report.year})? This action can be undone by an administrator.`)) {
      return;
    }

    try {
      await axios.delete(`/api/reports/${report.id}`);
      fetchReports();
      alert("Report deleted successfully.");
    } catch (error: any) {
      alert(
        "Failed to delete report: " +
          (error.response?.data?.error || error.message)
      );
    }
  };

  const handleGeneratePDF = async (
    report: Report,
    e: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    e.stopPropagation();
    const button = e.currentTarget;
    const originalText = button.textContent;

    try {
      // Show loading state
      button.textContent = "Generating...";
      button.disabled = true;

      // The report data is already in the report object
      await generateReportPDF(report, report.data || {}, report.church_plants || []);
    } catch (error: any) {
      console.error("Failed to generate PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      // Restore button state
      button.textContent = originalText;
      button.disabled = false;
    }
  };

  const handleDownloadAllPDFs = async (): Promise<void> => {
    if (reports.length === 0) {
      alert("No reports to download");
      return;
    }

    if (
      !window.confirm(
        `Download ${reports.length} PDF(s)? The PDFs will be saved individually in your Downloads folder.`
      )
    ) {
      return;
    }

    // Download PDFs sequentially with a small delay between each
    for (let i = 0; i < reports.length; i++) {
      try {
        console.log(`Generating PDF ${i + 1} of ${reports.length}`);
        await generateReportPDF(reports[i], reports[i].data || {}, reports[i].church_plants || []);

        // Add a delay between downloads to prevent overwhelming the browser
        if (i < reports.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000)); // 1 second delay
        }
      } catch (error: any) {
        console.error(
          `Failed to generate PDF for report ${reports[i].id}:`,
          error
        );
        // Continue with next report even if one fails
      }
    }
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusBadge = (status: string): React.ReactNode => {
    return (
      <span className={`status-badge ${status}`}>{status.toUpperCase()}</span>
    );
  };

  const months: MonthOption[] = [
    { value: "", label: "All Months" },
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const years: number[] = [];
  const currentYear = new Date().getFullYear();
  for (let year = 2024; year <= currentYear + 1; year++) {
    years.push(year);
  }

  // Sorting function
  const handleSort = (key: string): void => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Get sorted reports
  const getSortedReports = (): Report[] => {
    if (!sortConfig.key) return reports;

    const sortedReports = [...reports].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortConfig.key) {
        case "number":
          // Sort by missionary number (format: XX-YY-NNN)
          return sortConfig.direction === "asc"
            ? (a.missionary_number || "").localeCompare(b.missionary_number || "", undefined, { numeric: true })
            : (b.missionary_number || "").localeCompare(a.missionary_number || "", undefined, { numeric: true });
        case "missionary":
          aValue = a.missionary_name?.toLowerCase() || "";
          bValue = b.missionary_name?.toLowerCase() || "";
          break;
        case "location":
          // Sort by location, then by number within same location
          const locCmp = (a.state?.toLowerCase() || "").localeCompare(b.state?.toLowerCase() || "");
          if (locCmp !== 0) {
            return sortConfig.direction === "asc" ? locCmp : -locCmp;
          }
          return sortConfig.direction === "asc"
            ? (a.missionary_number || "").localeCompare(b.missionary_number || "", undefined, { numeric: true })
            : (b.missionary_number || "").localeCompare(a.missionary_number || "", undefined, { numeric: true });
        case "period":
          aValue = a.year * 100 + a.month;
          bValue = b.year * 100 + b.month;
          break;
        case "status":
          aValue = a.status?.toLowerCase() || "";
          bValue = b.status?.toLowerCase() || "";
          break;
        case "submitted":
          aValue = new Date(a.submission_date).getTime();
          bValue = new Date(b.submission_date).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return sortedReports;
  };

  const sortedReports = getSortedReports();

  // Calculate statistics
  const totalReports = reports.length;
  const pendingReports = reports.filter((r) => r.status === "review").length;
  const approvedReports = reports.filter((r) => r.status === "approved")
    .length;
  const rejectedReports = reports.filter((r) => r.status === "rejected")
    .length;

  if (loading) {
    return <div className="loading">Loading reports...</div>;
  }

  return (
    <div className="reports-container">
      <h2>Monthly Reports</h2>

      {/* Statistics Bar */}
      <div className="reports-stats">
        <div className="stat-item">
          <span className="stat-label">Total:</span>
          <span className="stat-value">{totalReports}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Pending:</span>
          <span className="stat-value pending">{pendingReports}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Approved:</span>
          <span className="stat-value approved">{approvedReports}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Rejected:</span>
          <span className="stat-value rejected">{rejectedReports}</span>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Year:</label>
          <select
            value={filters.year}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              handleFilterChange("year", e.target.value)
            }
          >
            <option value="">All Years</option>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Month:</label>
          <select
            value={filters.month}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              handleFilterChange("month", e.target.value)
            }
          >
            {months.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
        </div>

        {isAdmin && (
          <div className="filter-group">
            <label>Status:</label>
            <select
              value={filters.status}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                handleFilterChange("status", e.target.value)
              }
            >
              <option value="">All Status</option>
              <option value="review">Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        )}

        <div className="filter-group">
          <label>Missionary:</label>
          <select
            value={filters.missionaryId}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              handleFilterChange("missionaryId", e.target.value)
            }
          >
            <option value="">All Missionaries</option>
            {missionaries.map((missionary) => (
              <option key={missionary.id} value={missionary.id}>
                {missionary.name} ({missionary.number})
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={fetchReports}
          className="filter-btn"
          style={{
            padding: "8px 16px",
            backgroundColor: "#4299e1",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Apply Filters
        </button>

        <button
          onClick={handleDownloadAllPDFs}
          className="download-all-btn"
          disabled={reports.length === 0}
          style={{
            padding: "8px 16px",
            backgroundColor: reports.length === 0 ? "#cbd5e0" : "#48bb78",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: reports.length === 0 ? "not-allowed" : "pointer",
            marginLeft: "10px",
          }}
        >
          Download All PDFs ({reports.length})
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div
          className="error-message"
          style={{
            padding: "10px",
            backgroundColor: "#fed7d7",
            color: "#c53030",
            borderRadius: "4px",
            marginBottom: "20px",
          }}
        >
          {error}
        </div>
      )}

      {/* Reports Table */}
      <div className="reports-table">
        <table>
          <thead>
            <tr>
              <th
                onClick={() => handleSort("number")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Number{" "}
                <span
                  style={{
                    color:
                      sortConfig.key === "number" ? "inherit" : "#cbd5e0",
                    fontSize: "0.8em",
                  }}
                >
                  {sortConfig.key === "number"
                    ? sortConfig.direction === "asc"
                      ? "▲"
                      : "▼"
                    : "▼"}
                </span>
              </th>
              <th
                onClick={() => handleSort("missionary")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Missionary{" "}
                <span
                  style={{
                    color:
                      sortConfig.key === "missionary" ? "inherit" : "#cbd5e0",
                    fontSize: "0.8em",
                  }}
                >
                  {sortConfig.key === "missionary"
                    ? sortConfig.direction === "asc"
                      ? "▲"
                      : "▼"
                    : "▼"}
                </span>
              </th>
              <th
                onClick={() => handleSort("location")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Location{" "}
                <span
                  style={{
                    color:
                      sortConfig.key === "location" ? "inherit" : "#cbd5e0",
                    fontSize: "0.8em",
                  }}
                >
                  {sortConfig.key === "location"
                    ? sortConfig.direction === "asc"
                      ? "▲"
                      : "▼"
                    : "▼"}
                </span>
              </th>
              <th
                onClick={() => handleSort("period")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Period{" "}
                <span
                  style={{
                    color:
                      sortConfig.key === "period" ? "inherit" : "#cbd5e0",
                    fontSize: "0.8em",
                  }}
                >
                  {sortConfig.key === "period"
                    ? sortConfig.direction === "asc"
                      ? "▲"
                      : "▼"
                    : "▼"}
                </span>
              </th>
              <th>Ministry Stats</th>
              <th
                onClick={() => handleSort("status")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Status{" "}
                <span
                  style={{
                    color:
                      sortConfig.key === "status" ? "inherit" : "#cbd5e0",
                    fontSize: "0.8em",
                  }}
                >
                  {sortConfig.key === "status"
                    ? sortConfig.direction === "asc"
                      ? "▲"
                      : "▼"
                    : "▼"}
                </span>
              </th>
              <th
                onClick={() => handleSort("submitted")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Submitted{" "}
                <span
                  style={{
                    color:
                      sortConfig.key === "submitted" ? "inherit" : "#cbd5e0",
                    fontSize: "0.8em",
                  }}
                >
                  {sortConfig.key === "submitted"
                    ? sortConfig.direction === "asc"
                      ? "▲"
                      : "▼"
                    : "▼"}
                </span>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedReports.length === 0 ? (
              <tr>
                <td colSpan={8} className="no-data">
                  {filters.year ||
                  filters.month ||
                  filters.status ||
                  filters.missionaryId
                    ? "No reports found matching your filters"
                    : "No reports found"}
                </td>
              </tr>
            ) : (
              sortedReports.map((report) => {
                const reportData = report.data || {};
                const totalReached =
                  (reportData.heardGospel || 0) +
                  (reportData.receivedJesus || 0) +
                  (reportData.baptized || 0);

                return (
                  <tr
                    key={report.id}
                    onClick={() => handleViewReport(report.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <Link to={`/reports/${report.id}`} onClick={(e) => e.stopPropagation()}>
                        <strong>{report.missionary_number}</strong>
                      </Link>
                    </td>
                    <td>
                      <Link to={`/reports/${report.id}`} onClick={(e) => e.stopPropagation()}>
                        <strong>{report.missionary_name}</strong>
                      </Link>
                    </td>
                    <td>
                      {report.state}
                      <br />
                      <small style={{ color: "#718096" }}>
                        {report.location}
                      </small>
                    </td>
                    <td>
                      <strong>{months[report.month]?.label}</strong>
                      <br />
                      {report.year}
                    </td>
                    <td>
                      <div style={{ fontSize: "12px", color: "#4a5568" }}>
                        <div>Gospel: {reportData.heardGospel || 0}</div>
                        <div>Saved: {reportData.receivedJesus || 0}</div>
                        <div>Baptized: {reportData.baptized || 0}</div>
                      </div>
                    </td>
                    <td>{getStatusBadge(report.status)}</td>
                    <td>
                      {formatDate(report.submission_date)}
                      {report.approved_date && (
                        <div style={{ fontSize: "11px", color: "#1e7e34" }}>
                          Approved: {formatDate(report.approved_date)}
                        </div>
                      )}
                      {report.rejected_date && (
                        <div style={{ fontSize: "11px", color: "#c53030" }}>
                          Rejected: {formatDate(report.rejected_date)}
                        </div>
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="action-buttons">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewReport(report.id);
                          }}
                          className="view-btn"
                        >
                          View
                        </button>
                        <button
                          onClick={(e) => handleGeneratePDF(report, e)}
                          className="pdf-btn"
                          style={{
                            padding: "6px 12px",
                            backgroundColor: "#718096",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            fontSize: "14px",
                            cursor: "pointer",
                            marginLeft: "4px",
                          }}
                        >
                          PDF
                        </button>
                        {user.role === "admin" &&
                          report.status === "review" && (
                            <button
                              onClick={(e) => handleApproveReport(report.id, e)}
                              className="approve-btn"
                            >
                              Approve
                            </button>
                          )}
                        {(user.role === "admin" || user.role === "superadmin" || user.role === "system_admin") && (
                          <button
                            onClick={(e) => handleDeleteReport(report, e)}
                            className="delete-btn"
                            style={{
                              padding: "6px 12px",
                              backgroundColor: "#e53e3e",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              fontSize: "14px",
                              cursor: "pointer",
                              marginLeft: "4px",
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination or Load More could go here */}
      {reports.length > 0 && (
        <div
          style={{
            textAlign: "center",
            marginTop: "20px",
            color: "#718096",
            fontSize: "14px",
          }}
        >
          Showing {reports.length} report{reports.length !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
}

export default Reports;
